import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Smartphone, 
  Monitor, 
  MapPin, 
  Clock, 
  Shield, 
  AlertTriangle,
  LogOut
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  fetchActiveSessions,
  revokeSession,
  revokeAllOtherSessions,
  type SessionData,
} from "@/services/sessionService";
import { supabase } from "@/integrations/supabase/client";

const SessionManager = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    // Initial load
    loadSessions();

    // Set up realtime subscription for session changes with Safari protection
    let channel: ReturnType<typeof supabase.channel> | null = null;

    try {
      channel = supabase
        .channel('user-sessions-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'user_sessions',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            console.log('Session change detected:', payload);
            loadSessions();
          }
        )
        .subscribe();
    } catch (error) {
      console.warn('Failed to subscribe to session changes (Safari security restriction?):', error);
    }

    return () => {
      try {
        if (channel) supabase.removeChannel(channel);
      } catch (error) {
        console.warn('Error cleaning up session subscription:', error);
      }
    };
  }, [user]);

  const loadSessions = async () => {
    setIsLoading(true);
    try {
      const data = await fetchActiveSessions();
      setSessions(data);
    } catch (error) {
      console.error("Error fetching sessions:", error);
      toast.error("Failed to load sessions");
    } finally {
      setIsLoading(false);
    }
  };

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType) {
      case 'mobile':
        return <Smartphone className="h-5 w-5" />;
      case 'desktop':
        return <Monitor className="h-5 w-5" />;
      default:
        return <Monitor className="h-5 w-5" />;
    }
  };

  const handleEndSession = async (sessionId: string) => {
    try {
      const session = sessions.find(s => s.id === sessionId);
      if (session?.is_current) {
        toast.error("Cannot end current session. Please use Sign Out instead.");
        return;
      }

      await revokeSession(sessionId);
      toast.success("Session ended successfully");
      await loadSessions();
    } catch (error) {
      console.error("Error ending session:", error);
      toast.error("Failed to end session");
    }
  };

  const handleEndAllOtherSessions = async () => {
    try {
      await revokeAllOtherSessions();
      toast.success("All other sessions ended successfully");
      await loadSessions();
    } catch (error) {
      console.error("Error ending sessions:", error);
      toast.error("Failed to end sessions");
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Active Sessions</CardTitle>
          <CardDescription>Manage your active login sessions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="h-5 w-5 bg-muted rounded animate-pulse" />
                  <div className="space-y-1">
                    <div className="h-4 w-32 bg-muted rounded animate-pulse" />
                    <div className="h-3 w-24 bg-muted rounded animate-pulse" />
                  </div>
                </div>
                <div className="h-8 w-16 bg-muted rounded animate-pulse" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Active Sessions
        </CardTitle>
        <CardDescription>
          Manage your active login sessions across different devices
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {sessions.length === 0 ? (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                No active sessions found.
              </AlertDescription>
            </Alert>
          ) : (
            sessions.map((session) => (
              <div key={session.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  {getDeviceIcon(session.device_type)}
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {session.browser} on {session.os}
                      </span>
                      {session.is_current && (
                        <Badge variant="secondary" className="text-xs">
                          Current
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        <span>{session.ip_address}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>
                          {format(new Date(session.last_active), 'MMM d, HH:mm')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEndSession(session.id)}
                  disabled={session.is_current}
                  className="flex items-center gap-2"
                >
                  <LogOut className="h-4 w-4" />
                  {session.is_current ? 'Current' : 'End Session'}
                </Button>
              </div>
            ))
          )}
        </div>
        
        {sessions.length > 1 && (
          <div className="mt-6 pt-4 border-t">
            <Button 
              variant="destructive" 
              size="sm"
              onClick={handleEndAllOtherSessions}
            >
              End All Other Sessions
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SessionManager;